import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronRight, ChevronLeft, Upload, FileCheck2, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { GuidedCameraCapture } from "@/components/GuidedCameraCapture";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["Maklumat Ahli", "Maklumat Kursus", "Maklumat Waris", "Pengesahan"];
const DAERAH_LIST = ["Kuching", "Samarahan", "Sri Aman", "Sibu", "Bintulu", "Miri", "Lain-lain"];
const HUBUNGAN_LIST = ["Suami", "Isteri", "Anak", "Ibu", "Ayah", "Adik-beradik", "Saudara", "Lain-lain"];
const EDUCATION_LIST = ["UPSR", "PMR/PT3", "SPM", "STPM", "Sijil", "Diploma", "Ijazah Sarjana Muda", "Sarjana", "Lain-lain"];
const CURRENT_YEAR = new Date().getFullYear();
const SESSION_YEARS = Array.from({ length: CURRENT_YEAR + 1 - 2010 + 1 }, (_, i) => String(CURRENT_YEAR + 1 - i));
const STAFF_ROLES = ["super_admin", "secretariat", "president", "treasurer", "committee", "verifier"];

export default function Daftar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState("register");
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const { login, register } = useAuth();

  const [app, setApp] = useState(null);
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [capturingField, setCapturingField] = useState(null);

  const biasaCategoryId = useMemo(() => categories.find((c) => c.code === "BIASA")?.id, [categories]);
  const isStaff = user && STAFF_ROLES.includes(user.role);

  const loadApplication = useCallback(async () => {
    const res = await api.post("/applications/me");
    const data = { ...res.data };
    if (!data.phone) data.phone = "+60";
    if (!data.next_of_kin_phone) data.next_of_kin_phone = "+60";
    setApp(data);
    setForm(data);
    setStep(data.current_step || 1);
  }, []);

  useEffect(() => {
    if (isStaff) {
      navigate("/admin", { replace: true });
      return;
    }
    if (user) {
      loadApplication();
      api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }
  }, [user, isStaff, navigate, loadApplication]);

  useEffect(() => {
    if (biasaCategoryId && !form.membership_category_id) {
      setForm((f) => ({ ...f, membership_category_id: biasaCategoryId }));
    }
  }, [biasaCategoryId, form.membership_category_id]);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    const result = authMode === "register"
      ? await register(authForm.email, authForm.password, authForm.name)
      : await login(authForm.email, authForm.password);
    setAuthLoading(false);
    if (!result.success) setAuthError(result.error);
  };

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const STEP_FIELDS = {
    1: ["full_name", "identification_number", "gender", "address", "division_region", "email", "phone", "education_level", "occupation"],
    2: ["course_name", "course_date", "course_session_year", "course_venue"],
    3: ["next_of_kin_name", "next_of_kin_relationship", "next_of_kin_phone"],
  };

  const validateStep = (s) => {
    const fields = STEP_FIELDS[s];
    if (!fields) return true;
    const nextErrors = {};
    fields.forEach((f) => {
      if (!form[f] || String(form[f]).trim() === "") nextErrors[f] = "Ruangan ini wajib diisi";
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const persist = async (extra = {}) => {
    await api.put("/applications/me", { ...form, ...extra });
  };

  const saveStep = async (nextStep) => {
    if (nextStep > step && !validateStep(step)) {
      toast.error("Sila lengkapkan semua ruangan wajib sebelum meneruskan");
      return;
    }
    setSaving(true);
    try {
      await persist({ current_step: nextStep });
      setStep(nextStep);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await persist();
      toast.success("Draf permohonan disimpan");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingDraft(false);
    }
  };

  const uploadFile = async (file, field, isPublic = false, filename) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, filename || file.name || `${field}.jpg`);
      const res = await api.post(`/upload?is_public=${isPublic}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (["profile_photo_file_id", "ic_photo_file_id", "ic_photo_back_file_id"].includes(field)) {
        updateField(field, res.data.file_id);
        await api.put("/applications/me", { ...form, [field]: res.data.file_id });
      } else {
        await api.post(`/applications/me/documents?name=${encodeURIComponent(filename || file.name)}&file_id=${res.data.file_id}`);
        const refreshed = await api.get("/applications/me");
        setApp(refreshed.data);
        setForm((f) => ({ ...f, documents: refreshed.data.documents }));
      }
      toast.success("Fail berjaya dimuat naik");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = (e, field, isPublic = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadFile(file, field, isPublic);
  };

  const handleCameraCapture = (field, isPublic) => (blobOrFile, filename) => {
    setCapturingField(null);
    uploadFile(blobOrFile, field, isPublic, filename);
  };

  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      toast.error("Sila lengkapkan semua langkah sebelum menghantar");
      return;
    }
    if (!form.profile_photo_file_id || !form.ic_photo_file_id || !form.ic_photo_back_file_id) {
      toast.error("Sila muat naik Gambar Sendiri dan Gambar Kad Pengenalan (Depan & Belakang) sebelum menghantar");
      return;
    }
    if (!form.declaration_accepted || !form.data_agreement_accepted) {
      toast.error("Sila tandakan kedua-dua perakuan sebelum menghantar");
      return;
    }
    setSaving(true);
    try {
      await persist();
      const res = await api.post("/applications/me/submit");
      setApp(res.data);
      toast.success("Permohonan telah dihantar");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (isStaff) {
    return <div className="min-h-screen flex items-center justify-center text-piks-muted" data-testid="daftar-staff-redirect">Mengalihkan ke panel pentadbiran...</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-piks-black flex items-center justify-center px-4 py-12" data-testid="daftar-auth-gate">
        <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
          <h1 className="font-display text-xl font-bold text-piks-black mb-1">Mohon Keahlian PPPS</h1>
          <p className="text-sm text-piks-muted mb-6">Sila daftar akaun untuk memulakan permohonan keahlian anda.</p>
          <form onSubmit={handleAuthSubmit} className="space-y-4">
            {authMode === "register" && (
              <div>
                <Label>Nama Penuh</Label>
                <Input data-testid="daftar-auth-name-input" required value={authForm.name} onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })} className="mt-1.5" />
              </div>
            )}
            <div>
              <Label>Emel</Label>
              <Input type="email" data-testid="daftar-auth-email-input" required value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Kata Laluan</Label>
              <Input type="password" data-testid="daftar-auth-password-input" required value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} className="mt-1.5" />
            </div>
            {authError && <p className="text-sm text-piks-red">{authError}</p>}
            <Button type="submit" disabled={authLoading} data-testid="daftar-auth-submit-btn" className="w-full h-11 bg-piks-black hover:bg-piks-black/90">
              {authLoading ? "Sila tunggu..." : authMode === "register" ? "Daftar & Teruskan" : "Log Masuk & Teruskan"}
            </Button>
          </form>
          <p className="text-center text-sm text-piks-muted mt-6">
            <button onClick={() => setAuthMode(authMode === "register" ? "login" : "register")} data-testid="daftar-auth-toggle-btn" className="font-semibold text-piks-black hover:text-piks-red">
              {authMode === "register" ? "Sudah ada akaun? Log masuk" : "Belum ada akaun? Daftar"}
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (!app) {
    return <div className="min-h-screen flex items-center justify-center text-piks-muted">Memuatkan permohonan...</div>;
  }

  if (app.status !== "DRAF" && app.status !== "PERLU_MAKLUMAT_TAMBAHAN") {
    return (
      <div className="min-h-screen bg-piks-ivory flex items-center justify-center px-4 py-16" data-testid="daftar-status-view">
        <div className="max-w-md w-full bg-white rounded-2xl border border-piks-border p-8 text-center">
          {app.status === "DILULUSKAN" ? <Check className="w-12 h-12 text-emerald-600 mx-auto mb-4" /> : <FileCheck2 className="w-12 h-12 text-piks-gold mx-auto mb-4" />}
          <h1 className="font-display text-xl font-bold text-piks-black mb-2">Status Permohonan Keahlian</h1>
          <StatusBadge status={app.status} className="mx-auto mb-4" />
          <p className="text-sm text-piks-muted mb-6">
            {app.status === "DIHANTAR" && "Permohonan anda telah dihantar dan sedang menunggu semakan urusetia."}
            {app.status === "DALAM_SEMAKAN" && "Permohonan anda sedang dalam semakan aktif oleh urusetia."}
            {app.status === "DILULUSKAN" && "Tahniah! Permohonan anda telah diluluskan."}
            {app.status === "DITOLAK" && `Permohonan anda tidak berjaya diluluskan. ${app.rejection_reason || ""}`}
          </p>
          <Link to="/app"><Button className="bg-piks-black hover:bg-piks-black/90" data-testid="daftar-goto-dashboard-btn">Ke Dashboard Ahli</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-piks-ivory py-10 px-4" data-testid="daftar-wizard-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-display text-2xl font-bold text-piks-black mb-2 text-center">Permohonan Keahlian — Ahli Biasa</h1>
        <p className="text-sm text-piks-muted text-center mb-8">Lengkapkan semua langkah untuk menghantar permohonan anda.</p>

        {app.status === "PERLU_MAKLUMAT_TAMBAHAN" && app.review_notes && (
          <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="correction-notice">
            <strong className="block mb-1">Pembetulan Diperlukan:</strong> {app.review_notes}
          </div>
        )}

        <div className="flex items-center justify-between mb-10" data-testid="wizard-progress-indicator">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1 flex flex-col items-center relative">
              {i > 0 && <div className={`absolute top-3 right-1/2 w-full h-0.5 -z-10 ${step > i ? "bg-piks-gold" : "bg-piks-border"}`} />}
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-10 ${step > i + 1 ? "bg-piks-gold text-piks-black" : step === i + 1 ? "bg-piks-black text-white" : "bg-piks-border text-piks-muted"}`}>
                {step > i + 1 ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className="text-[10px] text-piks-muted mt-2 text-center">{label}</span>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-piks-border p-6 sm:p-8">
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              <h2 className="font-semibold text-piks-black mb-4">Maklumat Ahli</h2>
              <Field label="No. Ahli Sedia Ada (jika ada)" testid="existing_member_number" value={form.existing_member_number} onChange={(v) => updateField("existing_member_number", v)} hint="Kosongkan jika ini permohonan baharu" />
              <Field label="Nama Penuh" testid="full_name" required value={form.full_name} onChange={(v) => updateField("full_name", v)} error={errors.full_name} />
              <Field label="No. Kad Pengenalan" testid="identification_number" required value={form.identification_number} onChange={(v) => updateField("identification_number", v)} error={errors.identification_number} />
              <div>
                <Label>Jantina <span className="text-piks-red">*</span></Label>
                <Select value={form.gender} onValueChange={(v) => updateField("gender", v)}>
                  <SelectTrigger data-testid="select-gender" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih jantina" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lelaki">Lelaki</SelectItem>
                    <SelectItem value="Perempuan">Perempuan</SelectItem>
                  </SelectContent>
                </Select>
                {errors.gender && <p className="text-xs text-piks-red mt-1" data-testid="error-gender">{errors.gender}</p>}
              </div>
              <div>
                <Label>Alamat <span className="text-piks-red">*</span></Label>
                <Textarea data-testid="input-address" value={form.address || ""} onChange={(e) => updateField("address", e.target.value)} className="mt-1.5" />
                {errors.address && <p className="text-xs text-piks-red mt-1" data-testid="error-address">{errors.address}</p>}
              </div>
              <div>
                <Label>Daerah <span className="text-piks-red">*</span></Label>
                <Select value={form.division_region} onValueChange={(v) => updateField("division_region", v)}>
                  <SelectTrigger data-testid="select-daerah" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih daerah" /></SelectTrigger>
                  <SelectContent>
                    {DAERAH_LIST.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.division_region && <p className="text-xs text-piks-red mt-1" data-testid="error-division_region">{errors.division_region}</p>}
              </div>
              <Field label="Emel" type="email" testid="email" required value={form.email} onChange={(v) => updateField("email", v)} error={errors.email} />
              <Field label="No. Telefon" testid="phone" required value={form.phone || "+60"} onChange={(v) => updateField("phone", v)} error={errors.phone} />
              <div>
                <Label>Tahap Pendidikan <span className="text-piks-red">*</span></Label>
                <Select value={form.education_level} onValueChange={(v) => updateField("education_level", v)}>
                  <SelectTrigger data-testid="select-education-level" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih tahap pendidikan" /></SelectTrigger>
                  <SelectContent>
                    {EDUCATION_LIST.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.education_level && <p className="text-xs text-piks-red mt-1" data-testid="error-education_level">{errors.education_level}</p>}
              </div>
              <Field label="Pekerjaan" testid="occupation" required value={form.occupation} onChange={(v) => updateField("occupation", v)} error={errors.occupation} />
              {categories.length > 0 && (
                <div>
                  <Label>Kategori Keahlian</Label>
                  <Select value={form.membership_category_id} onValueChange={(v) => updateField("membership_category_id", v)}>
                    <SelectTrigger data-testid="select-membership-category" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-2">
              <h2 className="font-semibold text-piks-black mb-4">Maklumat Kursus</h2>
              <Field label="Nama Kursus" testid="course_name" required value={form.course_name} onChange={(v) => updateField("course_name", v)} error={errors.course_name} />
              <Field label="Tarikh Kursus" type="date" testid="course_date" required value={form.course_date} onChange={(v) => updateField("course_date", v)} error={errors.course_date} />
              <div>
                <Label>Sesi / Tahun <span className="text-piks-red">*</span></Label>
                <Select value={form.course_session_year} onValueChange={(v) => updateField("course_session_year", v)}>
                  <SelectTrigger data-testid="select-course-session-year" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih tahun" /></SelectTrigger>
                  <SelectContent>
                    {SESSION_YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.course_session_year && <p className="text-xs text-piks-red mt-1" data-testid="error-course_session_year">{errors.course_session_year}</p>}
              </div>
              <Field label="Tempat Kursus" testid="course_venue" required value={form.course_venue} onChange={(v) => updateField("course_venue", v)} error={errors.course_venue} />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-3">
              <h2 className="font-semibold text-piks-black mb-4">Maklumat Waris</h2>
              <Field label="Nama Waris" testid="next_of_kin_name" required value={form.next_of_kin_name} onChange={(v) => updateField("next_of_kin_name", v)} error={errors.next_of_kin_name} />
              <div>
                <Label>Hubungan <span className="text-piks-red">*</span></Label>
                <Select value={form.next_of_kin_relationship} onValueChange={(v) => updateField("next_of_kin_relationship", v)}>
                  <SelectTrigger data-testid="select-next-of-kin-relationship" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih hubungan" /></SelectTrigger>
                  <SelectContent>
                    {HUBUNGAN_LIST.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.next_of_kin_relationship && <p className="text-xs text-piks-red mt-1" data-testid="error-next_of_kin_relationship">{errors.next_of_kin_relationship}</p>}
              </div>
              <Field label="No. Telefon Waris" testid="next_of_kin_phone" required value={form.next_of_kin_phone || "+60"} onChange={(v) => updateField("next_of_kin_phone", v)} error={errors.next_of_kin_phone} />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="wizard-step-4">
              <h2 className="font-semibold text-piks-black mb-4">Pengesahan</h2>

              <div className="bg-piks-ivory rounded-lg p-4 text-sm text-piks-muted space-y-1.5" data-testid="wizard-summary">
                <p className="font-semibold text-piks-black text-xs uppercase tracking-wide mb-1">Maklumat Ahli</p>
                <p><strong className="text-piks-black">Nama:</strong> {form.full_name}</p>
                <p><strong className="text-piks-black">No. KP:</strong> {form.identification_number}</p>
                <p><strong className="text-piks-black">Jantina:</strong> {form.gender || "-"}</p>
                <p><strong className="text-piks-black">Alamat:</strong> {form.address}</p>
                <p><strong className="text-piks-black">Daerah:</strong> {form.division_region}</p>
                <p><strong className="text-piks-black">Emel:</strong> {form.email}</p>
                <p><strong className="text-piks-black">Telefon:</strong> {form.phone}</p>
                <p><strong className="text-piks-black">Tahap Pendidikan:</strong> {form.education_level}</p>
                <p><strong className="text-piks-black">Pekerjaan:</strong> {form.occupation}</p>
                <p className="font-semibold text-piks-black text-xs uppercase tracking-wide mb-1 pt-2">Maklumat Kursus</p>
                <p><strong className="text-piks-black">Kursus:</strong> {form.course_name}</p>
                <p><strong className="text-piks-black">Tarikh:</strong> {form.course_date}</p>
                <p><strong className="text-piks-black">Sesi/Tahun:</strong> {form.course_session_year}</p>
                <p><strong className="text-piks-black">Tempat:</strong> {form.course_venue}</p>
                <p className="font-semibold text-piks-black text-xs uppercase tracking-wide mb-1 pt-2">Maklumat Waris</p>
                <p><strong className="text-piks-black">Nama Waris:</strong> {form.next_of_kin_name}</p>
                <p><strong className="text-piks-black">Hubungan:</strong> {form.next_of_kin_relationship}</p>
                <p><strong className="text-piks-black">Telefon Waris:</strong> {form.next_of_kin_phone}</p>
              </div>

              <div>
                <Label>Gambar Diri <span className="text-piks-red">*</span></Label>
                <p className="text-xs text-piks-muted mt-1 mb-2">Semakan asas kecerahan &amp; kejelasan gambar sahaja (bukan pengesanan muka atau pengesahan identiti automatik).</p>
                {form.profile_photo_file_id && capturingField !== "profile_photo_file_id" ? (
                  <div className="flex items-center gap-3">
                    <img src={fileUrl(form.profile_photo_file_id)} alt="" className="w-14 h-14 rounded-full object-cover border border-piks-border" />
                    <Button type="button" variant="outline" size="sm" onClick={() => setCapturingField("profile_photo_file_id")} data-testid="retake-profile-photo-btn">Tukar Gambar</Button>
                  </div>
                ) : (
                  <GuidedCameraCapture mode="selfie" onCapture={handleCameraCapture("profile_photo_file_id", true)} onCancel={form.profile_photo_file_id ? () => setCapturingField(null) : null} />
                )}
                {!form.profile_photo_file_id && <p className="text-xs text-piks-red mt-1" data-testid="error-profile_photo_file_id">Ruangan ini wajib diisi</p>}
              </div>
              <div>
                <Label>Kad Pengenalan (Depan) <span className="text-piks-red">*</span></Label>
                <p className="text-xs text-piks-muted mt-1 mb-2">Semakan asas kecerahan &amp; kejelasan gambar sahaja.</p>
                {form.ic_photo_file_id && capturingField !== "ic_photo_file_id" ? (
                  <div className="flex items-center gap-3">
                    <img src={fileUrl(form.ic_photo_file_id)} alt="" className="w-16 h-12 rounded-md object-cover border border-piks-border" />
                    <Button type="button" variant="outline" size="sm" onClick={() => setCapturingField("ic_photo_file_id")} data-testid="retake-ic-photo-btn">Tukar Gambar</Button>
                  </div>
                ) : (
                  <GuidedCameraCapture mode="ic_front" onCapture={handleCameraCapture("ic_photo_file_id", false)} onCancel={form.ic_photo_file_id ? () => setCapturingField(null) : null} />
                )}
                {!form.ic_photo_file_id && <p className="text-xs text-piks-red mt-1" data-testid="error-ic_photo_file_id">Ruangan ini wajib diisi</p>}
              </div>
              <div>
                <Label>Kad Pengenalan (Belakang) <span className="text-piks-red">*</span></Label>
                <p className="text-xs text-piks-muted mt-1 mb-2">Semakan asas kecerahan &amp; kejelasan gambar sahaja.</p>
                {form.ic_photo_back_file_id && capturingField !== "ic_photo_back_file_id" ? (
                  <div className="flex items-center gap-3">
                    <img src={fileUrl(form.ic_photo_back_file_id)} alt="" className="w-16 h-12 rounded-md object-cover border border-piks-border" />
                    <Button type="button" variant="outline" size="sm" onClick={() => setCapturingField("ic_photo_back_file_id")} data-testid="retake-ic-photo-back-btn">Tukar Gambar</Button>
                  </div>
                ) : (
                  <GuidedCameraCapture mode="ic_back" onCapture={handleCameraCapture("ic_photo_back_file_id", false)} onCancel={form.ic_photo_back_file_id ? () => setCapturingField(null) : null} />
                )}
                {!form.ic_photo_back_file_id && <p className="text-xs text-piks-red mt-1" data-testid="error-ic_photo_back_file_id">Ruangan ini wajib diisi</p>}
              </div>
              <div>
                <Label>Dokumen Sokongan (Pilihan)</Label>
                <label className="mt-1.5 flex items-center gap-2 px-4 py-2 rounded-md border border-piks-border cursor-pointer text-sm hover:bg-piks-ivory w-fit">
                  <Upload className="w-4 h-4" /> Muat Naik Dokumen
                  <input type="file" className="hidden" data-testid="upload-document-input" onChange={(e) => handleFileUpload(e, "document")} />
                </label>
                {uploading && <p className="text-xs text-piks-muted mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Memuat naik...</p>}
                <ul className="mt-3 space-y-1.5">
                  {(form.documents || []).map((d, i) => (
                    <li key={i} className="text-xs text-piks-black flex items-center gap-1.5" data-testid={`uploaded-document-${i}`}><FileCheck2 className="w-3.5 h-3.5 text-emerald-600" /> {d.name}</li>
                  ))}
                </ul>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="declaration" data-testid="declaration-checkbox" checked={!!form.declaration_accepted} onCheckedChange={(v) => updateField("declaration_accepted", !!v)} className="mt-0.5" />
                <Label htmlFor="declaration" className="text-sm leading-relaxed text-piks-black">
                  Saya mengesahkan bahawa semua maklumat yang diberikan adalah benar dan tepat.
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox id="data_agreement" data-testid="data-agreement-checkbox" checked={!!form.data_agreement_accepted} onCheckedChange={(v) => updateField("data_agreement_accepted", !!v)} className="mt-0.5" />
                <Label htmlFor="data_agreement" className="text-sm leading-relaxed text-piks-black">
                  Saya bersetuju dengan penggunaan data peribadi saya mengikut terma keahlian dan perlembagaan PPPS.
                </Label>
              </div>
            </div>
          )}

          <div className="sticky bottom-0 z-10 bg-white flex flex-wrap justify-between gap-3 mt-8 pt-6 border-t border-piks-border">
            <Button variant="outline" disabled={step === 1 || saving} onClick={() => saveStep(step - 1)} data-testid="wizard-back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Kembali
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" disabled={savingDraft} onClick={saveDraft} data-testid="wizard-save-draft-btn">
                <Save className="w-4 h-4 mr-1.5" /> {savingDraft ? "Menyimpan..." : "Simpan Draf"}
              </Button>
              {step < 4 ? (
                <Button disabled={saving} onClick={() => saveStep(step + 1)} data-testid="wizard-next-btn" className="bg-piks-black hover:bg-piks-black/90">
                  {saving ? "Menyimpan..." : "Seterusnya"} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button disabled={saving || !form.declaration_accepted || !form.data_agreement_accepted || !form.profile_photo_file_id || !form.ic_photo_file_id || !form.ic_photo_back_file_id} onClick={handleSubmit} data-testid="wizard-submit-btn" className="bg-piks-red hover:bg-piks-red/90">
                  {saving ? "Menghantar..." : "Hantar Permohonan"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required = false, error, hint }) {
  return (
    <div>
      <Label>{label} {required && <span className="text-piks-red">*</span>}</Label>
      <Input type={type} data-testid={`input-${testid}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" />
      {hint && !error && <p className="text-xs text-piks-muted mt-1">{hint}</p>}
      {error && <p className="text-xs text-piks-red mt-1" data-testid={`error-${testid}`}>{error}</p>}
    </div>
  );
}
